import c from 'classnames';
import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setModalOpened } from 'store/modal';

import { Button, ButtonColors, TextColor, Typography } from '@repo/ui';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import { SLIDE_BACKGROUND_COLORS } from '../slideBackgroundColors';

import background from 'assets/images/summary/first-look/end_background.svg';

import { KitType } from '@repo/types';
import { AgeGroup, getAgeGroup } from '@repo/utils';
import styles from './End.module.scss';
import slideStyles from './Slides.module.scss';

const METRICS_GUT = [
  'Bifidobacterium',
  'Akkermansia',
  'Faecalibacterium',
  'Lactobacillaceae',
  'Abundance index',
  'Enterobacteriaceae',
  'Escherichia coli',
  'Salmonella enterica',
  'Streptococcus',
  'Candida',
];

const METRICS_VAG = [
  'Lactobacillus',
  'Gardnerella vaginalis',
  'Fannyhessea vaginae',
  'Chlamydia trachomatis',
  'Ureaplasma urealyticum',
  'Candida albicans',
];

const RowItem = ({ metric }: { metric: string }) => {
  return (
    <div className={styles.rowItem}>
      <Typography variant='body-s'>{metric}</Typography>
    </div>
  );
};

export const End: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const { currentKit, currentTinyAccount } = useCurrentKitAndTinyAccount();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isAdult =
    getAgeGroup(currentKit?.age_at_sampling ?? 0) === AgeGroup.ADULT;

  const useMetrics =
    currentKit?.sampling_type === KitType.VAGINAL ? METRICS_VAG : METRICS_GUT;
  const duplicatedMetrics = [
    ...useMetrics,
    ...useMetrics,
    ...useMetrics,
    ...useMetrics,
  ];

  const modalIdentifier = {
    name: 'firstLook',
    props: {},
  };

  const dismissModal = () => {
    dispatch(setModalOpened(modalIdentifier));
  };

  const handleViewActionPlan = () => {
    trackEvent('First Look', {
      event_type: 'button_clicked',
      button_text: 'View Action Plan',
      slide_id: 'end',
      action: 'view_action_plan',
    });
    dismissModal();
    if (currentKit?.id) {
      navigate(`/action-plan/${currentKit.id}`);
    }
  };

  const handleViewFullReport = () => {
    trackEvent('First Look', {
      event_type: 'button_clicked',
      button_text: 'View Full Report',
      slide_id: 'end',
      action: 'view_full_report',
    });
    dismissModal();
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let animationId: number;
    let scrollPosition = 0;
    const scrollSpeed = 0.3;
    const itemHeight = 48;
    const totalHeight = useMetrics.length * itemHeight;

    const animate = () => {
      scrollPosition += scrollSpeed;

      if (scrollPosition >= totalHeight) {
        scrollPosition = 0;
      }

      container.style.transform = `translateY(-${scrollPosition}px)`;
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, []);

  return (
    <div
      className={c(slideStyles.slideWrapper, styles.slideContainer)}
      style={{ backgroundColor: SLIDE_BACKGROUND_COLORS.end }}
    >
      <div className={styles.contentContainer}>
        <div className={styles.textContainer}>
          <Typography
            variant='heading-2xl'
            color={TextColor.DARKPURPLE}
            weight='medium'
          >
            Wait, there’s more!
          </Typography>
          <Typography variant='body-m'>
            Dive into the full report for more metrics, or go straight to the
            Action Plan to see what{' '}
            {isAdult ? 'you need' : `${currentTinyAccount?.first_name} needs`}{' '}
            to do.
          </Typography>
        </div>
        <div className={styles.imagesContainer}>
          <img
            src={background}
            alt='Results background'
            className={styles.backgroundImage}
          />
          <div className={styles.scrollWrapper}>
            <div className={styles.scrollViewport}>
              <div className={styles.rowItemContainer} ref={scrollContainerRef}>
                {duplicatedMetrics.map((metric, index) => (
                  <RowItem key={index} metric={metric} />
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.buttonsContainer}>
          <Button
            variant='solid'
            color={ButtonColors.PURPLE}
            onClick={handleViewActionPlan}
          >
            View Action Plan
          </Button>
          <Button
            variant='outline'
            color={ButtonColors.PURPLE}
            onClick={handleViewFullReport}
          >
            View Full Report
          </Button>
        </div>
      </div>
    </div>
  );
};
